// The lockup of the brand art as a canvas draw (brand.md §2, §8): the
// face, the set lines, the sign and the glow. The painters of `paint.ts`
// and the black cover of the game page (`ui/dockin.ts`) draw the lockup
// here. The module imports no ground painter, so the game page takes the
// lockup without the chart and the wall art.

import { LOCKUP } from '../gen/presentation';
import { drawSign } from '../ui/sign';
import { coverSignCss } from './launch';
import { stackedLockup, type LinePlace, type LockupGeom, type Measure } from './lockup';
import { BRAND, rgba } from './palette';

type Ctx = CanvasRenderingContext2D;

/** The CSS font of the lockup face at the type size `px`. */
export function lockupFont(px: number): string {
  return `${LOCKUP.weight} ${px}px "${LOCKUP.family}"`;
}

let faceLoad: Promise<void> | null = null;

/** The face of the name: the vendored JetBrains Mono 500 of the chart,
 *  under the family of `[presentation.lockup]` (brand.md §2.1). A page
 *  loads it before it measures a line. The page adds the face once, and
 *  every later call gets the same promise. */
export function loadLockupFace(): Promise<void> {
  faceLoad ??= (async () => {
    const face = new FontFace(LOCKUP.family, 'url(/fonts/jetbrains-mono-500-chart.woff2) format("woff2")', {
      weight: String(LOCKUP.weight), unicodeRange: 'U+20-7E, U+B7',
    });
    document.fonts.add(face);
    await face.load();
  })();
  return faceLoad;
}

/** The advance of a character in the lockup face, measured on a canvas. */
export function canvasMeasure(ctx: Ctx): Measure {
  const memo = new Map<string, number>();
  return (ch, px) => {
    const key = `${ch}|${px}`;
    let w = memo.get(key);
    if (w === undefined) {
      ctx.font = lockupFont(px);
      w = ctx.measureText(ch).width;
      memo.set(key, w);
    }
    return w;
  };
}

/** Draws a set line with its left edge at `x0 + place.x` and its base
 *  line at `y0 + place.base`, one character at a time. */
export function drawLine(ctx: Ctx, place: LinePlace, x0: number, y0: number, ink: string, glow: boolean): void {
  const { line } = place;
  ctx.save();
  ctx.font = lockupFont(line.px);
  ctx.fillStyle = ink;
  ctx.textBaseline = 'alphabetic';
  if (glow) {
    ctx.shadowColor = rgba(BRAND.accent, 0.38);
    ctx.shadowBlur = line.cap * 0.42;
  }
  [...line.text].forEach((ch, i) => {
    if (ch !== ' ') ctx.fillText(ch, x0 + place.x + line.xs[i], y0 + place.base);
  });
  ctx.restore();
}

/** Draws a lockup with its origin at (`x`, `y`): the sign with no plate,
 *  the name in the bright, the tag line in the text ink and the DEV line
 *  of the dev identity in amber, with no glow. `glow` gives the sign and
 *  the name the light of the map. */
export function drawLockup(ctx: Ctx, g: LockupGeom, x: number, y: number, glow: boolean): void {
  ctx.save();
  if (glow) {
    ctx.shadowColor = rgba(BRAND.accent, 0.45);
    ctx.shadowBlur = g.sign * 0.1;
  }
  drawSign(ctx, x + g.signX, y + g.signY, g.sign, { plate: false });
  ctx.restore();
  drawLine(ctx, g.name, x, y, BRAND.bright, glow);
  if (g.tag) drawLine(ctx, g.tag, x, y, BRAND.text, false);
  if (g.dev) drawLine(ctx, g.dev, x, y, BRAND.amber, false);
}

/** The stacked lockup of the black cover (brand.md §11.2), as a canvas at
 *  the device pixels of the screen: the launch image of §9 with no ground,
 *  drawn at the size of the screen and not scaled from a raster, so the
 *  name and the edge stay sharp at every size. The sign box is
 *  `coverSignCss` and its middle stands on the middle of the viewport. The
 *  canvas holds the lockup and its glow only; the ink of the cover fills
 *  the rest of the screen. The call returns null where the page has no 2D
 *  canvas. The face must be loaded first (`loadLockupFace`).
 *
 *  `vw` and `vh` are the viewport in CSS px, and `dpr` is the device pixel
 *  ratio. The canvas comes positioned for a fixed parent that fills the
 *  viewport, and it stands on whole device pixels. */
export function coverLockupCanvas(vw: number, vh: number, dpr: number, dev: boolean): HTMLCanvasElement | null {
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  const k = dpr > 0 ? dpr : 1;
  const b = coverSignCss(vw, vh) * k;
  const g = stackedLockup(b, canvasMeasure(ctx), undefined, dev);
  // The clear space of the stacked lockup holds the glow of the sign.
  const m = Math.ceil(LOCKUP.stacked.clear * b);
  const left = Math.round((vw * k) / 2 - g.w / 2) - m;
  const top = Math.round((vh * k) / 2 - b / 2) - m;
  c.width = Math.ceil(g.w) + 2 * m;
  c.height = Math.ceil(g.h) + 2 * m;
  drawLockup(ctx, g, m, m, true);
  Object.assign(c.style, {
    position: 'absolute', left: `${left / k}px`, top: `${top / k}px`,
    width: `${c.width / k}px`, height: `${c.height / k}px`,
  });
  return c;
}
